import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Checkbox, Alert, Card, Typography, Space } from 'antd';
import { ShopOutlined, LockOutlined, MailOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import axios from 'axios';
import { ENDPOINTS } from '../../helpers/api/apiConfig';

const { Title, Text } = Typography;

interface CompanyCreationFormProps {
    token?: string; // Token from URL parameter
    onBackToLogin: () => void;
}

const CompanyCreationForm: React.FC<CompanyCreationFormProps> = ({ token, onBackToLogin }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');

    useEffect(() => {
        // Pre-fill token if provided
        if (token) {
            form.setFieldsValue({ token });
        }
    }, [token, form]);

    const handleCreateCompany = async (values: any) => {
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            await axios.post(ENDPOINTS.CREATE_COMPANY, {
                token: values.token,
                companyName: values.companyName,
                email: values.email,
                password: values.password,
                gdprConsent: values.gdprConsent
            });

            setSuccess('Company and admin account created successfully! You can now login with your credentials.');
            form.resetFields();
            
        } catch (err: any) {
            if (err.response?.status === 403) {
                setError(err.response.data.message || 'Invalid or expired token');
            } else if (err.response?.status === 400) {
                setError(err.response.data.message || 'Please check your information');
            } else {
                setError(err.response?.data?.error || 'Company creation failed');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card className="company-creation-card" style={{ maxWidth: 600, margin: '0 auto' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div style={{ textAlign: 'center' }}>
                    <Title level={2}>
                        <ShopOutlined /> Create Your Company
                    </Title>
                    <Text type="secondary">Complete your company registration with the token provided by our sales team</Text>
                </div>

                {error && <Alert message={error} type="error" showIcon />}
                {success && <Alert message={success} type="success" showIcon />}

                <Form
                    form={form}
                    name="create-company"
                    onFinish={handleCreateCompany}
                    layout="vertical"
                    size="large"
                >
                    <Form.Item
                        name="token"
                        label="Company Registration Token"
                        rules={[{ required: true, message: 'Please enter your company token!' }]}
                    >
                        <Input
                            prefix={<SafetyCertificateOutlined />}
                            placeholder="Token provided by sales representative"
                            autoComplete="off"
                        />
                    </Form.Item>

                    <Form.Item
                        name="companyName"
                        label="Company Name"
                        rules={[{ required: true, message: 'Please enter your company name!' }]}
                    >
                        <Input
                            prefix={<ShopOutlined />}
                            placeholder="Your Company Name"
                            autoComplete="organization"
                        />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label="Admin Email"
                        rules={[
                            { required: true, message: 'Please enter your email!' },
                            { type: 'email', message: 'Please enter a valid email!' }
                        ]}
                    >
                        <Input
                            prefix={<MailOutlined />}
                            placeholder="admin@yourcompany.com"
                            autoComplete="email"
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label="Admin Password"
                        rules={[
                            { required: true, message: 'Please enter your password!' },
                            { min: 6, message: 'Password must be at least 6 characters!' }
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Create a secure password"
                            autoComplete="new-password"
                        />
                    </Form.Item>

                    <Form.Item
                        name="confirmPassword"
                        label="Confirm Password"
                        dependencies={['password']}
                        rules={[
                            { required: true, message: 'Please confirm your password!' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Passwords do not match!'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Confirm your password"
                            autoComplete="new-password"
                        />
                    </Form.Item>

                    <Form.Item
                        name="gdprConsent"
                        valuePropName="checked"
                        rules={[
                            { 
                                validator: (_, value) => 
                                    value ? Promise.resolve() : Promise.reject(new Error('GDPR consent is required'))
                            }
                        ]}
                    >
                        <Checkbox>
                            <Text style={{ fontSize: '12px' }}>
                                I consent to the processing of my personal data in accordance with the 
                                <a href="https://www.e-nano.io/samsss" target="_blank" rel="noopener noreferrer"> SaaS Availability, Maintenance & Support Services Terms</a> and 
                                <a href="https://www.e-nano.io/deviceandsaassubscriberterms" target="_blank" rel="noopener noreferrer"> Terms and Conditions</a>. 
                                This includes creating a company account and storing my admin credentials.
                            </Text>
                        </Checkbox>
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            block
                            size="large"
                            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                        >
                            Create Company & Admin Account
                        </Button>
                    </Form.Item>
                </Form>

                <div style={{ textAlign: 'center' }}>
                    <Text>Already have an account? </Text>
                    <Button type="link" onClick={onBackToLogin}>
                        Back to Login
                    </Button>
                </div>
            </Space>
        </Card>
    );
};

export default CompanyCreationForm;
